import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { IFile } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import { DeleteIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createQuotation } from '../../../../api/quotation/create-quotation'
import { deleteQuotations } from '../../../../api/quotation/delete-quotation'
import { getQuotations } from '../../../../api/quotation/get-quotations'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { Quotation } from '../../../../lib/types/models/quotation/quotation'
import { QuotationInput } from '../../../../lib/types/models/quotation/quotation-input'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import MediaAttachmentList from '../../../elements/media-attachment-list'
import QuotationForm from './quotation-form'

type Props =
  | {
      mode: 'teachingHall'
      teachingHallId: number
      coachCompanyId?: never
    }
  | {
      mode: 'coachCompany'
      coachCompanyId: number
      teachingHallId?: never
    }

const QuotationSection = ({ mode, teachingHallId, coachCompanyId }: Props) => {
  const queryClient = useQueryClient()
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'delete',
    Quotation
  >(['create', 'delete'])

  const { mutateAsync: createQuotationMutation } = useMutation({
    mutationFn: createQuotation,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotation'] })
      closeModel('create')
    }
  })

  const { data: quotations } = useQuery({
    queryKey: ['quotation'],
    queryFn: () => {
      if (mode === 'teachingHall') {
        return getQuotations({ filter: { teachingHallId: +teachingHallId } })
      }
      if (mode === 'coachCompany') {
        return getQuotations({ filter: { coachCompanyId: +coachCompanyId } })
      }
    }
  })

  const { mutateAsync: deleteQuotationMutation, isPending } = useMutation({
    mutationFn: deleteQuotations,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotation'] })
      closeModel('delete')
    }
  })

  const handleSubmit = (values: QuotationInput) => {
    if (mode === 'teachingHall') {
      return createQuotationMutation({ teachingHallId: +teachingHallId, ...values })
    }
    if (mode === 'coachCompany') {
      return createQuotationMutation({ coachCompanyId: +coachCompanyId, ...values })
    }
  }

  const hasQuotations = !!quotations?.data.length

  const files: IFile[] =
    quotations?.data.map((quotation) => ({
      name: quotation.file.name,
      path: quotation.file.path,
      size: quotation.file.size,
      type: quotation.file.type,
      caption: quotation.file.caption,
      extension: quotation.file.extension
    })) || []

  return (
    <>
      <MediaAttachmentList
        title="Quotations"
        files={hasQuotations ? files : []}
        getLabel={(file) => {
          // Find the matching quotation to get the name
          const quotation = quotations?.data.find((q) => q.file.path === file.path)
          return valueRender(quotation, (q) => q.name)
        }}
        getInfoSection={(file) => {
          // Find the matching quotation to get the validUntil date
          const quotation = quotations?.data.find((q) => q.file.path === file.path)
          return (
            <Badge
              variant="white"
              text={`Valid until : ${quotation?.validUntil ? DateFormatter.DDMMYYYY(quotation.validUntil) : '-'}`}
            />
          )
        }}
        onClick={() => toggleModal('create')}
        onDelete={(file) => {
          // Find the matching quotation to delete
          const quotation = quotations?.data.find((q) => q.file.path === file.path)
          if (quotation) {
            toggleModal('delete', quotation)
          }
        }}
      />

      <Modal
        isOpen={modalState.create}
        title={'Upload document'}
        icon={UploadIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <QuotationForm
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isEnglish: true,
            onConfirm: async (values) => {
              await handleSubmit(values)
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>
      {selectedData && (
        <Modal
          isOpen={modalState.delete}
          title={'Are you sure you want to delete this quotation?'}
          icon={DeleteIcon}
          variant="destructive"
          size="small"
          isLoading={isPending}
          actionButtonsOptions={{
            cancelText: 'Cancel',
            confirmText: 'Delete',
            onConfirm: async () => {
              await deleteQuotationMutation([+selectedData.id])
            }
          }}
          onCancel={() => closeModel('delete')}
        />
      )}
    </>
  )
}

export default QuotationSection
